// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Clatter — one round, decoupled from Trystero, the DOM and the clock (it is driven entirely by
// submit()/receive()/tick()), which is what makes the host-transfer and no-deadlock contracts
// testable without a network.
//
// THE WIRE IS THE WHOLE LOG, every time. A finished round is at most a couple of hundred small
// ints, so the cost is nothing, and in exchange a dropped datagram becomes unobservable: there is
// no delta to lose. A 2s heartbeat re-sends the same log, so a peer that goes quiet and comes back
// self-heals within one beat, and a peer that joins mid-round is instantly in sync.
//
// AND THERE IS NO HOST. The state is `replay(mode, seed, n, log)` — a pure function — so every peer
// holds the same authority, host transfer is a non-event, and a merge is a sorted union rather than
// a patch that could diverge. The only thing a peer ever does on someone else's behalf is fill in
// for a seat that has gone quiet, and because that fill-in is the deterministic shipped bot, two
// peers that both do it produce byte-identical bytes.

import {
  RULES_REV,
  aSeat,
  canonical,
  digest,
  isOver,
  isPending,
  legalActions,
  pendingSeats,
  replay,
  score,
  standings,
  type Breakdown,
  type PlyEvent,
  type State,
} from './rules';
import { modeOf, type Mode } from './modes';
import { choose, tierOf, type Tier } from './bot';

export interface WireMsg {
  /** Round number, so a message from the previous round cannot disturb this one. */
  r: number;
  /** The whole action log. */
  a: number[];
  /** Digest of the state after replaying it. */
  d: number;
  /** Rules revision — a mismatch is a build mismatch, not a desync. */
  v: number;
  /** The mode, and the seat count. Two peers disagreeing on either is a build problem. */
  m: string;
  n: number;
}

export type Fault = null | 'build' | 'desync';

export interface MatchDeps {
  send: (m: WireMsg) => void;
  onChange: () => void;
  onEvents: (events: PlyEvent[]) => void;
  onEnd: () => void;
}

export interface MatchOpts {
  modeId: string;
  seed: number;
  round: number;
  names: string[];
  n: number;
  /** null = spectator: it watches, and it never speaks. */
  mySeat: number | null;
  /** Solo only: every other seat is played by this tier locally. */
  botTier: Tier | null;
  clockMs?: number;
  deps: MatchDeps;
}

/** How long a seat may hold up the room, after its own clock, before somebody plays for it. */
const GRACE_MS = 5000;
/**
 * …and how long once we already KNOW it is gone. Waiting out the full decision clock plus the grace
 * for a peer whose tab we watched close costs about thirty seconds PER TURN, which for a round with
 * a dozen of its turns left is a survivor staring at a board for six minutes. Measured in the
 * two-tab smoke test, which is the only place it shows up.
 */
const MISSING_GRACE_MS = 1200;
/** Silence from the room before a peer is assumed gone. A live peer heartbeats every 2s. */
export const SILENCE_MS = 20_000;
/** The tier that plays for an absent seat. Fixed, so every peer computes the same move. */
export const FILL_TIER = 'steady';

export class Match {
  readonly mode: Mode;
  readonly seed: number;
  readonly round: number;
  readonly names: string[];
  readonly n: number;
  readonly mySeat: number | null;
  readonly botTier: Tier | null;
  readonly clockMs: number;
  state: State;
  fault: Fault = null;
  /** Seats believed to have left. Their turns are played by whichever peer notices. */
  readonly missing = new Set<number>();
  /** Ms left on the current decision, for the seats that still owe one. */
  decisionLeft = 0;
  /** Ms since anything at all was heard from the room. Only meaningful when networked. */
  silence = 0;
  private log: number[] = [];
  private seenEvents = 0;
  private ended = false;
  private readonly networked: boolean;
  deps: MatchDeps;

  constructor(o: MatchOpts) {
    this.mode = modeOf(o.modeId);
    this.seed = o.seed >>> 0;
    this.round = o.round;
    this.names = o.names;
    this.n = o.n;
    this.mySeat = o.mySeat;
    this.botTier = o.botTier;
    this.clockMs = o.clockMs ?? 0;
    this.decisionLeft = this.clockMs;
    this.deps = o.deps;
    this.networked = o.botTier === null;
    this.state = replay(this.mode.id, this.seed, this.n, []).state;
  }

  // ── queries the screen asks ───────────────────────────────────────────────────
  get over(): boolean {
    return isOver(this.state) || this.fault !== null;
  }

  /** True when the local player owes a decision right now. */
  get mine(): boolean {
    return this.mySeat !== null && this.fault === null && isPending(this.state, this.mySeat);
  }

  get waitingOn(): number[] {
    return this.fault === null ? pendingSeats(this.state) : [];
  }

  legal(): number[] {
    if (this.mySeat === null || this.fault !== null) return [];
    return legalActions(this.state, this.mySeat);
  }

  breakdown(seat: number): Breakdown {
    return score(this.state.sheets[seat], this.mode);
  }

  standings(): ReturnType<typeof standings> {
    return standings(this.state);
  }

  // ── driving it ────────────────────────────────────────────────────────────────
  /** Commit an action chosen locally — by the player, by a local bot, or by a clock. */
  submit(action: number): boolean {
    if (this.over) return false;
    const a = action >>> 0;
    if (!legalActions(this.state, aSeat(a)).includes(a)) return false;
    this.log.push(a);
    this.apply();
    this.broadcast();
    return true;
  }

  /** Re-send the whole log. Cheap, idempotent, and the reason a lost message is not a hang. */
  broadcast(): void {
    // A spectator holds no authority, so it never speaks. Otherwise a peer that joined mid-round
    // would broadcast whatever it has and look like a rival claim to everybody else.
    if (this.mySeat === null || this.fault !== null) return;
    this.deps.send({ r: this.round, a: this.log.slice(), d: digest(this.state), v: RULES_REV, m: this.mode.id, n: this.n });
  }

  receive(msg: WireMsg): void {
    if (!msg || msg.r !== this.round || this.fault !== null) return;
    // Anything at all counts as "the room is alive", including a heartbeat carrying a log we have.
    this.silence = 0;
    if (msg.v !== RULES_REV || msg.m !== this.mode.id || msg.n !== this.n) {
      this.fault = 'build';
      this.deps.onChange();
      return;
    }
    const incoming = Array.isArray(msg.a) ? msg.a : [];
    const mine = canonical(this.log);
    const theirs = canonical(incoming);
    const merged = canonical([...mine, ...theirs]);
    const eq = (x: number[], y: number[]): boolean => x.length === y.length && x.every((v, i) => v === y[i]);

    if (eq(merged, mine)) {
      // The merge taught us nothing. Two very different cases hide here, and conflating them
      // reports a desync at the exact moment the protocol is working.
      if (eq(theirs, mine)) {
        // We genuinely hold the same log. Disagreeing about the RESULT means one of us is not
        // running these rules.
        if (typeof msg.d === 'number' && msg.d !== digest(this.state)) {
          this.fault = 'desync';
          this.deps.onChange();
        }
      } else {
        // They hold a LOSING rival claim for a slot we resolved the other way — two peers both
        // filled in for the same silent seat, and canonical() kept ours. Their digest describes a
        // log we have already discarded, so comparing it would report a desync against a peer that
        // is about to agree with us. Send ours instead; they adopt it and we converge.
        this.broadcast();
      }
      return;
    }
    this.log = merged;
    this.apply();
    // Answer with our own view: if they were behind, this is how they catch up.
    if (incoming.length < merged.length) this.broadcast();
  }

  /**
   * Advance the wall clock. Everything time-based lives here and is driven by setInterval from the
   * screen — never rAF alone, which a backgrounded tab throttles to nothing.
   */
  tick(dtMs: number): void {
    if (this.over) return;

    if (this.networked) {
      this.silence += dtMs;
      if (this.silence >= SILENCE_MS) {
        // Trystero does not always report a tab that vanished, so silence is the other evidence.
        for (const seat of this.waitingOn) if (seat !== this.mySeat) this.missing.add(seat);
      }
    }

    if (this.clockMs <= 0) return;

    // A seat we already know has left does not get to hold the room for its full clock.
    const gone = this.waitingOn.filter((seat) => seat !== this.mySeat && this.missing.has(seat));
    if (gone.length > 0) {
      this.overdue += dtMs;
      if (this.overdue >= MISSING_GRACE_MS) this.playFor(gone[0]);
      else this.deps.onChange();
      return;
    }

    this.decisionLeft = Math.max(0, this.decisionLeft - dtMs);
    if (this.decisionLeft > 0) {
      this.deps.onChange();
      return;
    }

    // The clock ran out. Play for MY seat first — never a forfeit and never a freeze: the action
    // comes from the same evaluator every other action comes from, so lockstep is untouched.
    for (const seat of this.waitingOn) {
      if (seat === this.mySeat) {
        this.playFor(seat);
        return;
      }
    }
    // Somebody else's clock. Wait out a grace period before speaking for them — a phone changing
    // network takes a few seconds — then fill in, deterministically, so two peers that both notice
    // produce the identical action and the log dedupes it.
    this.overdue += dtMs;
    if (this.overdue >= GRACE_MS) {
      const seat = this.waitingOn[0];
      if (seat !== undefined) this.playFor(seat);
    }
  }

  private overdue = 0;

  /** The deterministic fill-in. Public so a test can drive a takeover without a network. */
  playFor(seat: number): boolean {
    if (this.over || !isPending(this.state, seat)) return false;
    const tier = seat === this.mySeat && this.botTier ? this.botTier : tierOf(FILL_TIER);
    const a = choose(this.state, seat, tier, this.seed + this.round * 7919);
    if (a < 0) return false;
    return this.submit(a);
  }

  /** Solo: play every seat that is not the local player's. */
  playBots(): boolean {
    if (this.botTier === null || this.over) return false;
    let did = false;
    for (const seat of this.waitingOn) {
      if (seat === this.mySeat) continue;
      const a = choose(this.state, seat, this.botTier, this.seed + this.round * 7919);
      if (a >= 0 && this.submit(a)) did = true;
    }
    return did;
  }

  peerLeft(seat: number): void {
    if (seat === this.mySeat) return;
    this.missing.add(seat);
    this.deps.onChange();
  }

  peerBack(seat?: number): void {
    this.silence = 0;
    if (seat === undefined) this.missing.clear();
    else this.missing.delete(seat);
    this.broadcast();
    this.deps.onChange();
  }

  private apply(): void {
    const r = replay(this.mode.id, this.seed, this.n, this.log);
    if (r.bad !== null) {
      // An action that is not legal here means the two sides are not running the same rules. That
      // is a stale cached build, and it must say so rather than diverge quietly.
      this.fault = 'build';
      this.deps.onChange();
      return;
    }
    this.state = r.state;
    this.log = canonical(this.log);
    this.decisionLeft = this.clockMs;
    this.overdue = 0;
    const fresh = r.events.slice(this.seenEvents);
    this.seenEvents = r.events.length;
    if (fresh.length > 0) this.deps.onEvents(fresh);
    this.deps.onChange();
    if (isOver(this.state) && !this.ended) {
      this.ended = true;
      this.deps.onEnd();
    }
  }
}

/**
 * Which seat a roster index takes in a given round. Rotating it means a match is fair even before
 * the head start does its work — over several rounds every player sits in every seat.
 */
export const seatForRound = (rosterIndex: number, round: number, n: number): number => (rosterIndex + round) % n;
